import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { normalizeText } from '../utils/text';

export type EntityOption = {
  id: string;
  label: string;
  /** Sous-texte dans la liste (ex. académie, commune, spécialité pour un utilisateur). */
  description?: string;
};

export type EntityAutocompleteProps = {
  label: string;
  options: EntityOption[];
  valueId: string;
  onChangeId: (id: string) => void;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  /** Si `valueId` n’est pas dans `options`, une entrée provisoire permet d’afficher la sélection. */
  orphanLabel?: string;
  loading?: boolean;
};

export function EntityAutocomplete({
  label,
  options,
  valueId,
  onChangeId,
  disabled,
  required,
  helperText,
  orphanLabel,
  loading,
}: EntityAutocompleteProps) {
  const mergedOptions = React.useMemo(() => {
    const trimmed = valueId.trim();
    if (!trimmed) return options;
    if (options.some((o) => o.id === trimmed)) return options;
    return [
      {
        id: trimmed,
        label: orphanLabel ?? 'Sélection actuelle (absente de la liste chargée)',
      } satisfies EntityOption,
      ...options,
    ];
  }, [options, valueId, orphanLabel]);

  const selected = valueId.trim()
    ? mergedOptions.find((o) => o.id === valueId.trim()) ?? null
    : null;

  return (
    <Autocomplete<EntityOption, false, boolean | undefined, false>
      loading={loading}
      options={mergedOptions}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opts, { inputValue }) => {
        const v = normalizeText(inputValue);
        if (!v) return opts;
        return opts.filter((o) => {
          const label = normalizeText(o.label);
          const desc = normalizeText(o.description ?? '');
          return label.includes(v) || desc.includes(v);
        });
      }}
      value={selected}
      onChange={(_, v) => onChangeId(v?.id ?? '')}
      disabled={disabled}
      autoHighlight
      disableClearable={!!required}
      noOptionsText="Aucun résultat"
      renderOption={(props, option) => {
        const { key: _optionKey, style, ...otherProps } = props;
        void _optionKey;
        const desc = option.description?.trim();
        const liStyle: React.CSSProperties = {
          ...(style && typeof style === 'object' ? style : {}),
          display: 'flex',
          alignItems: 'flex-start',
          overflow: 'visible',
          whiteSpace: 'normal',
          minHeight: 'auto',
          height: 'auto',
          width: '100%',
        };
        return (
          <li {...otherProps} key={option.id} style={liStyle}>
            <Box sx={{ width: '100%', minWidth: 0, py: 0.25 }}>
              <Typography variant="body2" component="span" sx={{ display: 'block' }}>
                {option.label}
              </Typography>
              {desc ? (
                <Typography
                  variant="caption"
                  component="span"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25, whiteSpace: 'normal', wordBreak: 'break-word' }}
                >
                  {desc}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} helperText={helperText} />
      )}
    />
  );
}
